import * as React from 'react';
import { css, html } from 'react-strict-dom';
import { Api } from 'src/api';
import { Text } from 'src/components';
import { Style } from 'src/constants';
import { color } from 'src/constants/color.css';
import { useTimerElapsed } from 'src/contexts';
import { Model } from 'src/models';
import { formatTimeSpent } from 'src/util';

import TimeLogEntryRow from './TimeLogEntryRow';
import TimeLogRunningRow from './TimeLogRunningRow';

type Entry = Model.WorkDone.Logged<Api.WorkDone.V_List>;

type Props = {
  label: string;
  day: Model.WorkDone.Day<Entry>;
  /** Heads the day with the clock still going, on today's card only. */
  showRunning: boolean;
  onEdit: (entry: Entry) => void;
  onDelete: (entry: Entry) => void;
};

export default function TimeLogDayCard({
  label,
  day,
  showRunning,
  onEdit,
  onDelete,
}: Props) {
  // Kept apart from the day's total, which only counts banked minutes.
  const elapsedSeconds = useTimerElapsed();
  const runningMinutes = showRunning ? Math.floor(elapsedSeconds / 60) : 0;

  return (
    <html.div style={styles.card}>
      <html.div
        style={[
          Style.row,
          Style.alignCenter,
          Style.spaceBetween,
          styles.header,
        ]}
      >
        <Text typeface="caption.semiBold" color="neutral60">
          {label}
        </Text>
        <html.div style={[Style.row, Style.alignCenter, styles.totals]}>
          {runningMinutes > 0 && (
            <html.span style={styles.running}>
              <Text typeface="caption.medium" color="green">
                {`+${formatTimeSpent(runningMinutes) ?? '0m'} running`}
              </Text>
            </html.span>
          )}
          <Text typeface="body.small.medium" color="neutral100">
            {formatTimeSpent(day.minutes) ?? '0m'}
          </Text>
        </html.div>
      </html.div>

      <html.span style={styles.rule} />

      {showRunning && <TimeLogRunningRow />}

      {day.workDones.map((workDone) => (
        <React.Fragment key={workDone.id}>
          <TimeLogEntryRow
            workDone={workDone}
            onEdit={() => onEdit(workDone)}
            onDelete={() => onDelete(workDone)}
          />
        </React.Fragment>
      ))}

      <html.span style={styles.bottomPadding} />
    </html.div>
  );
}

const styles = css.create({
  card: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: color.neutral0,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: color.neutral20,
    borderRadius: 12,
    // Deliberately not clipped, so a row's menu can open past the card.
  },
  header: {
    paddingInline: 16,
    paddingTop: 13,
    paddingBottom: 11,
  },
  totals: {
    gap: 8,
  },
  running: {
    paddingInline: 8,
    paddingBlock: 2,
    borderRadius: 999,
    backgroundColor: color.greenBg,
  },
  rule: {
    height: 1,
    backgroundColor: color.neutral20,
  },
  bottomPadding: {
    height: 8,
  },
});
